import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { ArrowRightLeft, ChevronDown, ChevronRight, Search } from 'lucide-react';

import Layout from '../../components/layout/AppLayout';
import TransferLendTabs from '../../components/inventory/TransferLendTabs';
import Modal from '../../../shared/components/ui/Modal';
import Header from '../../../shared/components/layout/Header';
import Loader from '../../../shared/components/ui/Loader';
import { useSidebar } from '../../../shared/contexts/SidebarContext';
import { useProjectsList } from '../../../../contexts/ProjectsContext';
import { useSmartBack, shouldShowModuleHomeBack } from '../../../shared/navigation';
import PaginationComponent from '../../../shared/components/PaginationComponent';
import { SegmentedControl, StatusPill } from '../../../shared/components/ui/primitives';
import {
  createStockTransfer,
  getInventoryListWithCounts,
  getStockTransfers,
  getTransferDestinations
} from '../../services/api/implementation/impl';
import {
  formatDate,
  todayISO,
  transferStatus,
  transferTypeLabel
} from '../../utils/stockTransfers';
import {
  getSavedInventoryProjectId,
  saveInventoryProjectId,
  useProjectWarehouses
} from '../../hooks/useInventorySelection';

const QTY_PATTERN = /^\d*\.?\d{0,3}$/;
const roundQty = (value) => Math.round((Number(value) || 0) * 1000) / 1000;
const errorMessage = (err, fallback) => err?.response?.data?.message || err?.message || fallback;
const PAGE_SIZE = 20;
// The shared Modal takes a % width; go near full width on phones.
const modalWidth = (desktop) =>
  typeof window !== 'undefined' && window.innerWidth < 768 ? 96 : desktop;

const StockTransfers = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { collapsed } = useSidebar();
  const { onBack } = useSmartBack({ fallback: '/builder/inventory' });
  const showHomeBack = shouldShowModuleHomeBack('inventory', location);
  const { projects, projectsForDropdown, isProjectsLoading } = useProjectsList();
  const queryClient = useQueryClient();

  const [projectId, setProjectId] = useState(
    () => Number(location.state?.projectID) || getSavedInventoryProjectId() || ''
  );
  const [fromId, setFromId] = useState('');
  const [toId, setToId] = useState('');
  const [search, setSearch] = useState('');
  const [quantities, setQuantities] = useState({}); // { [inventory_id]: '12.5' }
  const [note, setNote] = useState('');
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState(null);
  // Phase 3: inside this project, or to another project of the same company.
  const [scope, setScope] = useState(() =>
    location.state?.scope === 'other' ? 'other' : 'project'
  ); // 'project' | 'other'
  const [kind, setKind] = useState(() =>
    location.state?.scope === 'other' && location.state?.kind === 'lend' ? 'lend' : 'transfer'
  ); // 'transfer' | 'lend' (other project only)
  const [destProjectId, setDestProjectId] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [typeFilter, setTypeFilter] = useState(''); // '' | 'transfer' | 'lend'
  // The transfer / lend form lives in a modal; Warehouses and Lends open it on arrival.
  const [formOpen, setFormOpen] = useState(() => location.state?.openForm === true);
  const [focusId, setFocusId] = useState(null); // transfer a notification opened

  // A notification opens one transfer: its project, then the row in the list.
  // An effect so it also works when this page is already open.
  useEffect(() => {
    const linkedProject = Number(searchParams.get('projectID'));
    if (!linkedProject) return;
    setProjectId(linkedProject);
    setTypeFilter('');
    setPage(1);
    setFocusId(Number(searchParams.get('id')) || null);
  }, [searchParams]);

  useEffect(() => {
    if (!projects?.length) return;
    if (!projectId || !projects.some((p) => Number(p.id) === Number(projectId))) {
      setProjectId(getSavedInventoryProjectId(projects) || projects[0].id);
    }
  }, [projectId, projects]);

  useEffect(() => {
    if (projectId) saveInventoryProjectId(projectId);
  }, [projectId]);

  const {
    active: warehouses,
    defaultWarehouse,
    isFetching: loadingWarehouses
  } = useProjectWarehouses(projectId);

  // New project: start over, "from" pre-filled with the default warehouse.
  const defaultId = defaultWarehouse?.id || '';
  useEffect(() => {
    setFromId(defaultId);
    setToId('');
    setQuantities({});
    setNote('');
    setPage(1);
    setDestProjectId('');
    setDueDate('');
  }, [projectId, defaultId]);

  // Own warehouses plus the company's other projects and their warehouses.
  const { data: destinations, isFetching: loadingDestinations } = useQuery({
    queryKey: ['transfer-destinations', projectId],
    queryFn: () => getTransferDestinations(projectId),
    enabled: !!projectId,
    retry: false,
    staleTime: 60 * 1000
  });
  const otherProjects = useMemo(
    () => (destinations?.projects || []).filter((p) => !p.same_project),
    [destinations]
  );
  const destProject = otherProjects.find((p) => Number(p.project_id) === Number(destProjectId));
  const destWarehouses = destProject?.warehouses || [];
  const crossProject = scope === 'other';

  // Project names for the past-transfers list: the user's projects, then the company's.
  const projectNames = useMemo(() => {
    const map = new Map();
    (destinations?.projects || []).forEach((p) => map.set(Number(p.project_id), p.project_name));
    (projects || []).forEach((p) => map.set(Number(p.id), p.name || p.label));
    return map;
  }, [destinations, projects]);
  const projectName = (id) => projectNames.get(Number(id)) || `Project #${id}`;

  const changeScope = (value) => {
    setScope(value);
    setToId('');
    setDestProjectId('');
    setDueDate('');
    if (value === 'project') setKind('transfer');
  };

  const openForm = (mode) => {
    changeScope(mode === 'lend' ? 'other' : 'project');
    setKind(mode === 'lend' ? 'lend' : 'transfer');
    setFormOpen(true);
  };

  // Closing drops the draft; the modal asks first when there is one.
  const closeForm = () => {
    setFormOpen(false);
    setQuantities({});
    setNote('');
    setDueDate('');
  };

  const changeDestProject = (value) => {
    setDestProjectId(value ? Number(value) : '');
    const next = otherProjects.find((p) => Number(p.project_id) === Number(value));
    // Pre-fill the receiving project's default warehouse.
    const preset = next?.warehouses?.find((w) => w.is_default) || next?.warehouses?.[0];
    setToId(preset ? Number(preset.id) : '');
  };

  const changeFrom = (value) => {
    setFromId(value ? Number(value) : '');
    setQuantities({});
    if (Number(value) === Number(toId)) setToId('');
  };

  const { data: stockRes, isFetching: loadingStock } = useQuery({
    queryKey: ['transfer-from-stock', projectId, fromId],
    queryFn: () =>
      getInventoryListWithCounts(projectId, { warehouseId: fromId, status: 'all', limit: 500 }),
    enabled: !!projectId && !!fromId
  });

  // Only rows with stock on hand can be moved.
  const stockRows = useMemo(
    () => (stockRes?.items || []).filter((row) => Number(row.physicalStock) > 0),
    [stockRes]
  );
  const visibleRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return stockRows;
    return stockRows.filter((row) =>
      `${row.item_name || ''} ${row.category_name || ''}`.toLowerCase().includes(q)
    );
  }, [stockRows, search]);

  const chosen = stockRows.filter((row) => String(quantities[row.id] ?? '').trim() !== '');

  const { data: transfersRes, isFetching: loadingTransfers } = useQuery({
    queryKey: ['stock-transfers', projectId, page, typeFilter],
    queryFn: () =>
      getStockTransfers({ project_id: projectId, type: typeFilter, page, limit: PAGE_SIZE }),
    enabled: !!projectId
  });
  const transfers = transfersRes?.items || [];
  const totalPages = Math.max(1, Math.ceil((transfersRes?.total || 0) / PAGE_SIZE));

  useEffect(() => {
    const match = focusId && transfers.find((t) => Number(t.id) === focusId);
    if (match) {
      setExpanded(match.id);
      setFocusId(null);
    }
  }, [focusId, transfers]);

  const submitMutation = useMutation({
    mutationFn: () => {
      if (crossProject && !destProjectId) throw new Error('Pick the project to send stock to');
      if (!fromId || !toId) throw new Error('Pick both warehouses');
      if (Number(fromId) === Number(toId)) throw new Error('Pick two different warehouses');
      const isLend = crossProject && kind === 'lend';
      if (isLend && dueDate && dueDate < todayISO()) {
        throw new Error('The due date can’t be in the past');
      }
      if (!chosen.length) throw new Error('Enter a quantity for at least one item');
      const items = chosen.map((row) => {
        const text = String(quantities[row.id]).trim();
        const qty = Number(text);
        if (!QTY_PATTERN.test(text) || !(qty > 0)) {
          throw new Error(`Quantity for ${row.item_name} must be more than 0, up to 3 decimals`);
        }
        if (qty > Number(row.physicalStock)) {
          throw new Error(`Only ${roundQty(row.physicalStock)} of ${row.item_name} in stock`);
        }
        return { inventory_id: row.id, quantity: qty };
      });
      return createStockTransfer({
        from_warehouse_id: Number(fromId),
        to_warehouse_id: Number(toId),
        type: isLend ? 'lend' : 'transfer',
        ...(isLend && dueDate && { due_date: dueDate }),
        ...(note.trim() && { note: note.trim() }),
        items
      });
    },
    onSuccess: (saved) => {
      const isLend = saved?.type === 'lend' || (crossProject && kind === 'lend');
      toast.success(
        isLend
          ? `Stock lent to ${destProject?.project_name || 'the project'}`
          : crossProject
            ? `Stock transferred to ${destProject?.project_name || 'the project'}`
            : 'Stock transferred'
      );
      closeForm();
      setPage(1);
      queryClient.invalidateQueries({ queryKey: ['lends'] });
      queryClient.invalidateQueries({ queryKey: ['transfer-from-stock'] });
      queryClient.invalidateQueries({ queryKey: ['stock-transfers'] });
      queryClient.invalidateQueries({ queryKey: ['projectWarehouses'] });
      queryClient.invalidateQueries({ queryKey: ['AllInventories'] });
      queryClient.invalidateQueries({ queryKey: ['inventoryByItem'] });
    },
    onError: (err) => toast.error(errorMessage(err, 'Could not transfer stock'))
  });

  const selectClass =
    'h-10 min-w-[12rem] rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-800';

  return (
    <Layout>
      <Loader
        isVisible={
          isProjectsLoading ||
          loadingWarehouses ||
          loadingStock ||
          loadingDestinations ||
          submitMutation.isPending
        }
      />
      <div
        className={`fixed top-0 z-10 bg-white ${
          collapsed ? 'left-[76px] w-[calc(100%-76px)]' : 'left-64 w-[calc(100%-16rem)]'
        }`}>
        <Header
          data={{ name: 'Transfers & Lends' }}
          backArrow={showHomeBack}
          onBack={showHomeBack ? onBack : undefined}
        />
      </div>

      <div className="px-4 pb-8 pt-[5.5rem] sm:px-6">
        <TransferLendTabs active="/builder/inventory/transfers" projectId={projectId} />
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-[#1E4A53]">Transfers and lends</h2>
            <p className="text-sm text-gray-500">
              Move stock between this project&apos;s warehouses, or send it to another project.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => openForm('transfer')}
              className="h-10 rounded-lg bg-[#2A565E] px-4 text-sm font-semibold text-white">
              Transfer stock
            </button>
            <button
              type="button"
              onClick={() => openForm('lend')}
              className="h-10 rounded-lg border border-[#2A565E] px-4 text-sm font-semibold text-[#2A565E]">
              Lend stock
            </button>
            <select
              value={projectId || ''}
              onChange={(event) => setProjectId(Number(event.target.value))}
              className={selectClass}>
              {(projectsForDropdown || []).map((project) => (
                <option key={project.id} value={project.id}>
                  {project.label || project.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {formOpen && (
          <Modal
            name={crossProject ? 'Send stock to another project' : 'Move stock between warehouses'}
            width={modalWidth(70)}
            onClose={closeForm}
            isDirty={chosen.length > 0 || note.trim() !== ''}
            confirmOnClose>
            <p className="mb-4 text-sm text-gray-500">
              {crossProject
                ? 'Transfer it for good, or lend it and get it back later. Same company only.'
                : 'Both warehouses must be in the same project.'}
            </p>
            <SegmentedControl
              ariaLabel="Where the stock goes"
              className="mb-4 max-w-full overflow-x-auto"
              value={scope}
              onChange={changeScope}
              options={[
                { value: 'project', label: 'Inside this project' },
                { value: 'other', label: 'To another project' }
              ]}
            />

            {crossProject && destinations && !destinations.hasCompany ? (
              <div className="rounded-2xl border border-amber-100 bg-amber-50 px-4 py-4 text-sm text-amber-800">
                This project isn&apos;t linked to a company, so it can&apos;t send stock to other
                projects. Stock can only move between projects of the same company.
              </div>
            ) : crossProject && !loadingDestinations && otherProjects.length === 0 ? (
              <div className="rounded-2xl border border-amber-100 bg-amber-50 px-4 py-4 text-sm text-amber-800">
                No other project of this company has an active warehouse to send stock to.
              </div>
            ) : !crossProject && !loadingWarehouses && warehouses.length < 2 ? (
              <div className="rounded-2xl border border-amber-100 bg-amber-50 px-4 py-4 text-sm text-amber-800">
                This project has only one active warehouse. Add another one to transfer stock, or
                send it to another project.
                <button
                  type="button"
                  onClick={() => changeScope('other')}
                  className="ml-3 font-semibold text-[#2A565E] underline">
                  To another project
                </button>
                <button
                  type="button"
                  onClick={() =>
                    navigate('/builder/inventory/warehouses', {
                      state: { projectID: Number(projectId) }
                    })
                  }
                  className="ml-3 font-semibold text-[#2A565E] underline">
                  Open warehouses
                </button>
              </div>
            ) : (
              <div>
                <div className="flex flex-wrap items-end gap-3">
                  <label className="text-sm">
                    <span className="mb-1 block text-gray-600">From</span>
                    <select
                      value={fromId}
                      onChange={(e) => changeFrom(e.target.value)}
                      className={selectClass}>
                      <option value="">Select warehouse</option>
                      {warehouses.map((w) => (
                        <option key={w.id} value={w.id}>
                          {w.name}
                          {w.is_default ? ' (default)' : ''}
                        </option>
                      ))}
                    </select>
                  </label>
                  <ArrowRightLeft size={18} className="mb-3 hidden text-gray-400 sm:block" />
                  {crossProject && (
                    <label className="text-sm">
                      <span className="mb-1 block text-gray-600">To project</span>
                      <select
                        value={destProjectId}
                        onChange={(e) => changeDestProject(e.target.value)}
                        className={selectClass}>
                        <option value="">Select project</option>
                        {otherProjects.map((p) => (
                          <option key={p.project_id} value={p.project_id}>
                            {p.project_name}
                          </option>
                        ))}
                      </select>
                    </label>
                  )}
                  <label className="text-sm">
                    <span className="mb-1 block text-gray-600">
                      {crossProject ? 'To warehouse' : 'To'}
                    </span>
                    <select
                      value={toId}
                      disabled={crossProject && !destProjectId}
                      onChange={(e) => setToId(e.target.value ? Number(e.target.value) : '')}
                      className={`${selectClass} disabled:bg-gray-50`}>
                      <option value="">Select warehouse</option>
                      {(crossProject ? destWarehouses : warehouses)
                        .filter((w) => crossProject || Number(w.id) !== Number(fromId))
                        .map((w) => (
                          <option key={w.id} value={w.id}>
                            {w.name}
                            {w.is_default ? ' (default)' : ''}
                          </option>
                        ))}
                    </select>
                  </label>
                  <label className="flex h-10 w-full items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 sm:ml-auto sm:w-auto">
                    <Search size={16} className="text-gray-400" />
                    <input
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search item"
                      className="w-full bg-transparent text-sm outline-none sm:w-48"
                    />
                  </label>
                </div>

                {crossProject && (
                  <div className="mt-4 flex flex-wrap items-end gap-3">
                    <div className="text-sm">
                      <span className="mb-1 block text-gray-600">Send as</span>
                      <SegmentedControl
                        size="sm"
                        ariaLabel="Transfer or lend"
                        value={kind}
                        onChange={(value) => {
                          setKind(value);
                          if (value !== 'lend') setDueDate('');
                        }}
                        options={[
                          { value: 'transfer', label: 'Transfer (for good)' },
                          { value: 'lend', label: 'Lend (comes back)' }
                        ]}
                      />
                    </div>
                    {kind === 'lend' && (
                      <label className="text-sm">
                        <span className="mb-1 block text-gray-600">Due back by (optional)</span>
                        <input
                          type="date"
                          min={todayISO()}
                          value={dueDate}
                          onChange={(e) => setDueDate(e.target.value)}
                          className="h-10 rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-800"
                        />
                      </label>
                    )}
                    <p className="basis-full text-xs text-gray-500">
                      {kind === 'lend'
                        ? 'The other project owes this stock back. It shows under Lends until it is returned, or until you let them keep it.'
                        : 'The stock moves to the other project for good, at this warehouse’s average rate.'}
                    </p>
                  </div>
                )}

                <div className="mt-4 max-h-[22rem] overflow-auto rounded-lg border border-gray-100">
                  <table className="min-w-full text-left text-sm">
                    <thead className="sticky top-0 bg-[#F4F7F8] text-xs uppercase text-gray-500">
                      <tr>
                        <th className="px-4 py-3">Item</th>
                        <th className="px-4 py-3">Unit</th>
                        <th className="px-4 py-3">In stock</th>
                        <th className="px-4 py-3">
                          {crossProject && kind === 'lend' ? 'Lend qty' : 'Transfer qty'}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {!fromId ? (
                        <tr>
                          <td colSpan={4} className="px-4 py-8 text-center text-gray-400">
                            Pick the warehouse to move stock from
                          </td>
                        </tr>
                      ) : visibleRows.length === 0 ? (
                        <tr>
                          <td colSpan={4} className="px-4 py-8 text-center text-gray-400">
                            {loadingStock ? 'Loading…' : 'No items in stock here'}
                          </td>
                        </tr>
                      ) : (
                        visibleRows.map((row) => {
                          const value = quantities[row.id] ?? '';
                          const tooMuch = value !== '' && Number(value) > Number(row.physicalStock);
                          return (
                            <tr key={row.id} className="border-t border-gray-100">
                              <td className="px-4 py-2">
                                <p className="font-medium text-gray-900">{row.item_name}</p>
                                <p className="text-xs text-gray-500">{row.category_name || ''}</p>
                              </td>
                              <td className="px-4 py-2">{row.unit || '—'}</td>
                              <td className="px-4 py-2">{roundQty(row.physicalStock)}</td>
                              <td className="px-4 py-2">
                                <input
                                  type="number"
                                  min={0}
                                  step="0.001"
                                  max={row.physicalStock}
                                  value={value}
                                  onChange={(e) =>
                                    setQuantities((prev) => ({ ...prev, [row.id]: e.target.value }))
                                  }
                                  className={`h-9 w-28 rounded-lg border px-2 outline-none focus:border-[#2A565E] ${
                                    tooMuch ? 'border-red-500' : 'border-gray-200'
                                  }`}
                                />
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="mt-4 flex flex-wrap items-end gap-3">
                  <label className="min-w-[14rem] flex-1 text-sm">
                    <span className="mb-1 block text-gray-600">Note (optional)</span>
                    <input
                      maxLength={255}
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      placeholder="e.g. Moving cement to Yard 2 for block B"
                      className="h-10 w-full rounded-lg border border-gray-200 px-3 outline-none focus:border-[#2A565E]"
                    />
                  </label>
                  <button
                    type="button"
                    disabled={submitMutation.isPending || !chosen.length || !toId}
                    onClick={() => submitMutation.mutate()}
                    className="h-10 rounded-lg bg-[#2A565E] px-5 text-sm font-semibold text-white disabled:opacity-60">
                    {(() => {
                      const isLend = crossProject && kind === 'lend';
                      if (submitMutation.isPending) return isLend ? 'Lending…' : 'Transferring…';
                      return `${isLend ? 'Lend' : 'Transfer'}${
                        chosen.length ? ` ${chosen.length} item(s)` : ''
                      }`;
                    })()}
                  </button>
                </div>
              </div>
            )}
          </Modal>
        )}

        <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 px-4 py-3">
            <h3 className="text-sm font-semibold text-[#1E4A53]">Past transfers and lends</h3>
            <SegmentedControl
              size="sm"
              ariaLabel="Type"
              value={typeFilter}
              onChange={(value) => {
                setTypeFilter(value);
                setPage(1);
                setExpanded(null);
              }}
              options={[
                { value: '', label: 'All' },
                { value: 'transfer', label: 'Transfers' },
                { value: 'lend', label: 'Lends' }
              ]}
            />
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-[#F4F7F8] text-xs uppercase text-gray-500">
                <tr>
                  <th className="w-8 px-4 py-3" />
                  <th className="px-4 py-3">ID</th>
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">From</th>
                  <th className="px-4 py-3">To</th>
                  <th className="px-4 py-3">Items</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Note</th>
                </tr>
              </thead>
              <tbody>
                {transfers.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-4 py-8 text-center text-gray-400">
                      {loadingTransfers ? 'Loading…' : 'No transfers yet'}
                    </td>
                  </tr>
                ) : (
                  transfers.map((t) => {
                    const open = expanded === t.id;
                    const isLend = t.type === 'lend';
                    const otherProject =
                      t.from_project_id &&
                      t.to_project_id &&
                      Number(t.from_project_id) !== Number(t.to_project_id);
                    const outgoing = Number(t.from_project_id) === Number(projectId);
                    const status = transferStatus(t.status);
                    return (
                      <React.Fragment key={t.id}>
                        <tr
                          className="cursor-pointer border-t border-gray-100 hover:bg-gray-50"
                          onClick={() => setExpanded(open ? null : t.id)}>
                          <td className="px-4 py-3 text-gray-500">
                            {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                          </td>
                          <td className="px-4 py-3 font-medium text-gray-900">#{t.id}</td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            {t.created_at ? new Date(t.created_at).toLocaleString('en-IN') : '—'}
                          </td>
                          <td className="px-4 py-3">
                            <span className="font-medium text-gray-900">
                              {transferTypeLabel(t.type)}
                            </span>
                            {otherProject && (
                              <span className="block whitespace-nowrap text-xs text-gray-500">
                                {outgoing
                                  ? `To ${projectName(t.to_project_id)}`
                                  : `From ${projectName(t.from_project_id)}`}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            {t.fromWarehouse?.name || '—'}
                            {otherProject && (
                              <span className="block text-xs text-gray-500">
                                {projectName(t.from_project_id)}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            {t.toWarehouse?.name || '—'}
                            {otherProject && (
                              <span className="block text-xs text-gray-500">
                                {projectName(t.to_project_id)}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3">{t.items?.length || 0}</td>
                          <td className="px-4 py-3">
                            <StatusPill dense tone={status.tone}>
                              {status.label}
                            </StatusPill>
                            {isLend && t.due_date && (
                              <span className="block whitespace-nowrap text-xs text-gray-500">
                                Due {formatDate(t.due_date)}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-gray-600">{t.note || '—'}</td>
                        </tr>
                        {open && (
                          <tr className="bg-gray-50">
                            <td />
                            <td colSpan={8} className="px-4 py-3">
                              {isLend && (
                                <button
                                  type="button"
                                  onClick={() =>
                                    navigate('/builder/inventory/lends', {
                                      state: { projectID: Number(projectId) }
                                    })
                                  }
                                  className="mb-2 text-xs font-semibold text-[#2A565E] hover:underline">
                                  Open in Lends
                                </button>
                              )}
                              <table className="min-w-[24rem] text-left text-xs">
                                <thead className="text-gray-500">
                                  <tr>
                                    <th className="py-1 pr-6">Item</th>
                                    <th className="py-1 pr-6">Qty</th>
                                    {isLend && <th className="py-1 pr-6">Returned</th>}
                                    <th className="py-1 pr-6">Rate</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(t.items || []).map((item) => (
                                    <tr key={item.id}>
                                      <td className="py-1 pr-6 text-gray-800">
                                        {item.fromInventory?.item_name ||
                                          `#${item.from_inventory_id}`}
                                      </td>
                                      <td className="py-1 pr-6">
                                        {roundQty(item.quantity)} {item.fromInventory?.unit || ''}
                                      </td>
                                      {isLend && (
                                        <td className="py-1 pr-6">{roundQty(item.qty_returned)}</td>
                                      )}
                                      <td className="py-1 pr-6">
                                        {item.rate == null
                                          ? '—'
                                          : `₹${Number(item.rate).toLocaleString('en-IN', {
                                              maximumFractionDigits: 2
                                            })}`}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
        {(transfersRes?.total || 0) > PAGE_SIZE && (
          <div className="mt-2">
            <PaginationComponent
              page={page}
              totalPages={totalPages}
              onPageChange={(p) => setPage(Math.min(Math.max(1, p), totalPages))}
              isFetching={loadingTransfers}
            />
          </div>
        )}
      </div>
    </Layout>
  );
};

export default StockTransfers;
